import { useState, useEffect, useRef, Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { userEmail } from "../lib/helpers";
import { User, Languages, Compass, LogOut } from "lucide-react";
import { WhatsNew, SEEN_KEY } from "../components/WhatsNew";
import { Tour, TOUR_KEY } from "../components/Tour";
import { getPref, setPref } from "../lib/userPrefs";
import { RELEASES } from "../lib/releases";

// Introduktionen startar av sig själv bara för lärare som skapat kontot efter
// att den släpptes — befintliga lärare får nyheten via "Nytt i tRAT" i stället.
const TOUR_SINCE = "2026-10-20T00:00:00Z";

// ─────────────────────────────────────────
//  TEACHER LAYOUT (sidebar + topbar)
// ─────────────────────────────────────────
// Map nav item ids to URL paths
const NAV_PATHS = { home: "/", teacher: "/create", courses: "/courses", questionbank: "/questionbank", profile: "/profile", superadmin: "/superadmin" };
const PATH_TO_NAV = Object.fromEntries(Object.entries(NAV_PATHS).map(([k, v]) => [v, k]));

function TeacherLayout({ user, isSuperAdmin, canAdminister, lang, setLang, theme, setTheme, G, css, children, onCreateFresh, modalOpen = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Derive active screen from pathname
  const screen = PATH_TO_NAV[location.pathname] || (location.pathname.startsWith("/courses/") ? "courses" : location.pathname.startsWith("/dashboard/") ? "dashboard" : location.pathname.startsWith("/superadmin") ? "superadmin" : "home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef(null);
  const [showTour, setShowTour] = useState(false);
  const [prefsVersion, setPrefsVersion] = useState(0);

  // Ny lärare som inte sett introduktionen: starta den när inget annat
  // (välkomst- eller hjälpruta) ligger öppet.
  useEffect(() => {
    if (!user || modalOpen || getPref(user, TOUR_KEY)) return;
    if (new Date(user.created_at || 0) < new Date(TOUR_SINCE)) return;
    const id = setTimeout(() => setShowTour(true), 600); // låt sidan rita klart först
    return () => clearTimeout(id);
  }, [user, modalOpen]);

  function closeTour() {
    setShowTour(false);
    if (!getPref(user, TOUR_KEY)) {
      setPref(user, TOUR_KEY, true);
      // En ny lärare behöver inte gamla nyheter som olästa
      if (!getPref(user, SEEN_KEY) && RELEASES[0]) setPref(user, SEEN_KEY, RELEASES[0].id);
      setPrefsVersion(v => v + 1);
    }
  }

  // Close avatar dropdown on outside click
  useEffect(() => {
    if (!avatarOpen) return;
    const handler = (e) => { if (avatarRef.current && !avatarRef.current.contains(e.target)) setAvatarOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [avatarOpen]);

  // Close mobile menu on location change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const navItems = [
    { id: "home",        label: lang === "sv" ? "Översikt" : "Overview",     icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 6.5L8 2l6 4.5V14a.5.5 0 01-.5.5H10V10H6v4.5H2.5A.5.5 0 012 14V6.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg> },
    { id: "teacher",     label: lang === "sv" ? "Skapa tRAT" : "Create tRAT",   icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.3"/><path d="M8 5v6M5 8h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg> },
    { id: "courses",     label: lang === "sv" ? "Mina kurser" : "My courses",  icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 3h12v10H2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><path d="M2 6h12M6 6v7" stroke="currentColor" strokeWidth="1.3"/></svg> },
    { id: "questionbank",label: lang === "sv" ? "Frågebank" : "Question bank",    icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 2h10a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.3"/><path d="M5 5h6M5 8h6M5 11h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg> },
    { id: "profile",     label: lang === "sv" ? "Min profil" : "My profile",  icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1a3 3 0 100 6 3 3 0 000-6z" stroke="currentColor" strokeWidth="1.3"/><path d="M2 13.5c0-2.485 2.686-4.5 6-4.5s6 2.015 6 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg> },
    ...(canAdminister ? [{ id: "superadmin", label: lang === "sv" ? "Administration" : "Administration", icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1l1.5 3 3.5.5-2.5 2.5.5 3.5L8 9l-3 1.5.5-3.5L3 4.5 6.5 4z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>, dividerBefore: true }] : []),
  ];

  const displayName = user?.user_metadata?.full_name || userEmail(user)?.split("@")[0] || "Användare";
  const initials = displayName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  const SidebarLink = ({ item }) => {
    const active = screen === item.id;
    return (
      <button
        data-tour={`nav-${item.id}`}
        onClick={() => { if (item.id === "teacher" && onCreateFresh) { onCreateFresh(); } else { navigate(NAV_PATHS[item.id] || "/"); } }}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          width: "100%", background: active ? `${G.gold}12` : "none",
          border: "none", borderRadius: 8,
          color: active ? G.gold : G.text,
          opacity: active ? 1 : 0.75,
          fontFamily: "'DM Sans', sans-serif", fontWeight: active ? 600 : 500,
          fontSize: 14, padding: "9px 12px", cursor: "pointer",
          textAlign: "left", transition: "all 0.15s",
        }}
        onMouseEnter={e => { if (!active) { e.currentTarget.style.background = G.surface2; e.currentTarget.style.opacity = "1"; } }}
        onMouseLeave={e => { if (!active) { e.currentTarget.style.background = "none"; e.currentTarget.style.opacity = "0.75"; } }}
      >
        <span style={{ width: 3, height: 16, borderRadius: 2, background: active ? G.gold : "transparent", flexShrink: 0 }} />
        <span style={{ opacity: active ? 1 : 0.6, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20 }}>{item.icon}</span>
        {item.label}
      </button>
    );
  };

  const SIDEBAR_W = 220;
  const TOPBAR_H = 52;

  return (
    <div style={{ minHeight: "100vh", background: G.bg, color: G.text, fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── TOPBAR ─────────────────────────────────── */}
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: TOPBAR_H, background: G.surface, borderBottom: `1px solid ${G.border}`, display: "flex", alignItems: "center", padding: "0 16px", zIndex: 100, gap: 12 }}>
        
        {/* Mobile: hamburger */}
        <button
          onClick={() => setMobileMenuOpen(o => !o)}
          style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", padding: 4 }}
          className="mobile-menu-btn"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d={mobileMenuOpen ? "M4 4l12 12M16 4L4 16" : "M3 5h14M3 10h14M3 15h14"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </button>

        {/* Logo */}
        <button onClick={() => navigate("/")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 8, transition: "opacity 0.15s" }} onMouseEnter={e => e.currentTarget.style.opacity = "0.7"} onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
          <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 15, color: G.gold, letterSpacing: "0.04em" }}>tRAT</span>
        </button>

        <div style={{ flex: 1 }} />

        {/* Nytt i tRAT */}
        <div data-tour="whats-new">
          <WhatsNew key={prefsVersion} user={user} lang={lang} G={G} suppressTeaser={showTour || modalOpen} />
        </div>

        {/* Theme toggle */}
        <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 8, color: G.muted, padding: "6px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}>
          {theme === "dark"
            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
          }
        </button>

        {/* Avatar dropdown */}
        <div ref={avatarRef} style={{ position: "relative" }}>
          <button
            onClick={() => setAvatarOpen(o => !o)}
            style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: `1px solid ${avatarOpen ? G.gold : G.border}`, borderRadius: 8, padding: "5px 10px", cursor: "pointer", color: G.text, transition: "border-color 0.15s" }}
          >
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: `${G.gold}20`, border: `1px solid ${G.gold}40`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 12, color: G.gold, flexShrink: 0 }}>
              {initials}
            </div>
            <span style={{ fontSize: 14, fontWeight: 500, color: G.text, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} className="avatar-name">{displayName}</span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: avatarOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>
              <path d="M2 4l4 4 4-4" stroke={G.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {avatarOpen && (
            <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, minWidth: 200, boxShadow: "0 8px 32px rgba(0,0,0,0.3)", overflow: "hidden", zIndex: 200 }}>
              {/* User info */}
              <div style={{ padding: "12px 16px", borderBottom: `1px solid ${G.border}` }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: G.text, margin: 0 }}>{displayName}</p>
                <p style={{ fontSize: 12, color: G.muted, margin: "2px 0 0", fontFamily: "'DM Mono', monospace" }}>{userEmail(user)}</p>
              </div>
              {/* Menu items */}
              {[
                { label: "Min profil", Icon: User, action: () => { navigate("/profile"); setAvatarOpen(false); } },
                { label: "Språk: " + (lang === "sv" ? "Svenska" : "English"), Icon: Languages, action: () => { setLang(lang === "sv" ? "en" : "sv"); setAvatarOpen(false); } },
                { label: lang === "sv" ? "Visa introduktion" : "Show the tour", Icon: Compass, action: () => { setAvatarOpen(false); setShowTour(true); } },
              ].map(item => (
                <button key={item.label} onClick={item.action} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", background: "none", border: "none", padding: "10px 16px", cursor: "pointer", color: G.text, fontSize: 14, fontFamily: "'DM Sans', sans-serif", textAlign: "left" }}
                  onMouseEnter={e => e.currentTarget.style.background = G.surface2}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >
                  <item.Icon size={15} color={G.muted} aria-hidden="true" /> {item.label}
                </button>
              ))}
              <div style={{ borderTop: `1px solid ${G.border}` }}>
                <button onClick={() => supabase.auth.signOut()} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", background: "none", border: "none", padding: "10px 16px", cursor: "pointer", color: G.red, fontSize: 14, fontFamily: "'DM Sans', sans-serif", textAlign: "left" }}
                  onMouseEnter={e => e.currentTarget.style.background = G.surface2}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >
                  <LogOut size={15} aria-hidden="true" />
                  Logga ut
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── SIDEBAR (desktop) ──────────────────────── */}
      <div style={{ position: "fixed", top: TOPBAR_H, left: 0, width: SIDEBAR_W, height: `calc(100vh - ${TOPBAR_H}px)`, background: G.surface, borderRight: `1px solid ${G.border}`, padding: "16px 12px", overflowY: "auto", zIndex: 90 }} className="sidebar-desktop">
        <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {navItems.map((item, i) => (
            <Fragment key={item.id}>
              {item.dividerBefore && <div style={{ height: 1, background: G.border, margin: "8px 0" }} />}
              <SidebarLink item={item} />
            </Fragment>
          ))}
        </nav>
      </div>

      {/* ── MOBILE MENU OVERLAY ───────────────────── */}
      {mobileMenuOpen && (
        <div style={{ position: "fixed", inset: 0, top: TOPBAR_H, zIndex: 95 }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} onClick={() => setMobileMenuOpen(false)} />
          <div style={{ position: "absolute", top: 0, left: 0, width: 260, height: "100%", background: G.surface, borderRight: `1px solid ${G.border}`, padding: "16px 12px", overflowY: "auto" }}>
            <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {navItems.map((item, i) => (
                <Fragment key={item.id}>
                  {item.dividerBefore && <div style={{ height: 1, background: G.border, margin: "8px 0" }} />}
                  <SidebarLink item={item} />
                </Fragment>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* ── MAIN CONTENT ──────────────────────────── */}
      <style>{`
        @media (max-width: 900px) {
          .sidebar-desktop { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
          .teacher-main { margin-left: 0 !important; }
          .avatar-name { display: none !important; }
          .whatsnew-label { display: none !important; }
        }
        @media (min-width: 901px) {
          .mobile-menu-btn { display: none !important; }
        }
      `}</style>
      <div className="teacher-main" style={{ marginLeft: SIDEBAR_W, paddingTop: TOPBAR_H, minHeight: "100vh" }}>
        {children}
      </div>
      {showTour && <Tour lang={lang} G={G} onClose={closeTour} />}
    </div>
  );
}

export { TeacherLayout };
